/**
 * Centralized feature flag name constants.
 *
 * This module provides a single source of truth for feature flag names
 * used throughout the frontend. Using constants prevents typos and
 * makes it easy to find all usages of a particular flag.
 */

export const FeatureFlags = {
  // New guardrail flags
  RAG_ENABLED: 'RAG_enabled',
  WORKFLOWS_CHAT_ENABLED: 'workflows_chat_enabled',
  WORKFLOWS_FULL_ENABLED: 'workflows_full_enabled',
  AGENT_CREATION_ENABLED: 'agent_creation_enabled',
  APP_COMPANION_ENABLED: 'app_companion_enabled',
  OCP_TOOLS_ENABLED: 'ocp_tools_enabled',
  EXTERNAL_INTEGRATIONS_ENABLED: 'external_integrations_enabled',
  HEADLESS_ENABLED: 'headless_enabled',
  COLLABORATION_ENABLED: 'collaboration_enabled',
  ALERTING_ENABLED: 'alerting_enabled',
  CREDIT_LIMITING_ENABLED: 'credit_limiting_enabled',
  PRIVATE_AGENTS_ENABLED: 'private_agents_enabled',
  SHERLOCK: 'guardrails',
  WATSON: 'guardrails',
  AUTH_CONNECTION_TOOL_STATUS: 'auth_connection_tool_status',

  // Existing flags (for union checks with new flags)
  WORKFLOW_AGENT: 'workflow-agent',
  APP_COMPANION: 'app_companion',
  AGENT_DIRECTORY_PUBLISHER: 'agentdirectorypublisher',
  PLUGIN_PUBLISHER: 'plugin_publisher',
  AGENT_RESUME_ENABLED: 'agent_resume_enabled',
  AGENTS_V2: 'agents_v2',
  LLM_PROVIDER_SELECTOR: 'llm_provider',
  VOICE_DICTATION: 'voice_dictation',
  UX_FEATURES: 'ux_features',
  MEMORY_VISIBILITY: 'opal_memory_visibility',
  AUTH_PROVIDER_DISPLAY_NAME: 'auth_provider_display_name',
  OVERRIDE_CONNECTOR_TILE_NAME: 'override_connector_tile_name',
  CONNECTORS: 'connectors',
  DYNAMIC_OCP_DIRECTORY_URL: 'dynamic_ocp_directory_url',
  OPAL_CHAT_OVERLAY: 'opal_chat_overlay',
  ARTIFACTS: 'opal_artifacts',
  PLUGINS: 'plugins',
  AGENT_LIBRARY: 'agent_library',

  // ACL / Permissions
  PERMISSION: 'permission',

  // SDK / Developer
  AGENT_SDK: 'agent_sdk',

  // MISC
  CORS_DOMAIN: 'cors_domain',
  NOTIFICATIONS: 'notifications',
  MESSAGING_ENABLED: 'messaging_enabled',
  INSTRUCTION_TO_SKILLS: 'instruction_to_skills',
  SKILL_UX: 'skill_ux',
  WORKFLOW_UX: 'workflow_ux',
} as const;

export type FeatureFlagName = (typeof FeatureFlags)[keyof typeof FeatureFlags];

/**
 * Variables for the `ux_features` Optimizely feature flag.
 * Each key maps to a boolean variable that gates a specific UX feature.
 */
export const UxFeaturesVariables = {
  REORG_RAG_CONTEXT: 'reorg_rag_context',
  EXPORT_SPECIALIZED_AGENTS_LOGS: 'export_specialized_agents_logs',
} as const;

/**
 * Boolean variables for the `notifications` Optimizely feature flag.
 *
 * Empty today: the flag's original variables (grouping, badge_count,
 * browser_push, agent_watchers) are GA and were inlined as always-on. Add new
 * notification flag variables here and read them via the
 * `useNotificationFeatures` hook (features/notifications/hooks/useNotificationFeatures.ts).
 */
export const NotificationsVariables = {} as const;

/**
 * Variables for the `workflow_ux` Optimizely feature flag.
 */
export const WorkflowUxVariables = {
  COMBINE_AGENT_LOGS: 'combine_agent_logs',
  NATURAL_LANGUAGE_CONDITIONS: 'natural_language_conditions',
  REVAMPED_NAVIGATION_LAYOUT: 'revamped_navigation_layout',
} as const;
